import * as openpgp from "openpgp";
import {
  getVaultKeys,
  saveVaultKey,
  deleteVaultKey,
  getVaultStatusSync,
} from "./key-vault";

export type KeyPair = {
  id: string;
  fingerprint: string;
  name: string;
  email: string;
  privateKeyArmored: string;
  publicKeyArmored: string;
  revocationCertificate?: string;
  createdAt: string;
  expiresAt?: string | null;
  registryStatus?: "unpublished" | "published" | "pending" | null;
};

/**
 * Get all stored keys from the encrypted vault.
 * Returns cached in-memory keys (vault must be unlocked).
 * Falls back to empty array if vault is locked.
 */
export function getStoredKeys(): KeyPair[] {
  if (getVaultStatusSync() !== "unlocked") return [];
  return getVaultKeys();
}

/**
 * Save a key to the encrypted vault.
 * Fire-and-forget async write to maintain backward compatibility.
 */
export function saveKey(key: KeyPair) {
  saveVaultKey(key).catch((err) =>
    console.error("Failed to save key to vault:", err),
  );
}

/**
 * Delete a key from the encrypted vault by ID.
 * Fire-and-forget async write to maintain backward compatibility.
 */
export function deleteKey(id: string) {
  deleteVaultKey(id).catch((err) =>
    console.error("Failed to delete key from vault:", err),
  );
}

export async function generateNewKeyPair(
  name: string,
  email: string,
  passphrase: string,
): Promise<KeyPair> {
  const { privateKey, publicKey, revocationCertificate } =
    await openpgp.generateKey({
      type: "rsa", // or 'ecc'
      rsaBits: 4096,
      userIDs: [{ name, email }],
      passphrase,
    });

  const key = await openpgp.readKey({ armoredKey: publicKey });
  const id = key.getKeyID().toHex().toUpperCase();
  const fingerprint = key.getFingerprint().toUpperCase();
  const creationTime = key.getCreationTime();
  const expirationTime = await key.getExpirationTime();

  return {
    id,
    fingerprint,
    name,
    email,
    privateKeyArmored: privateKey,
    publicKeyArmored: publicKey,
    revocationCertificate,
    createdAt: new Date(creationTime).toISOString(),
    expiresAt:
      expirationTime && expirationTime !== Infinity
        ? new Date(expirationTime).toISOString()
        : null,
    registryStatus: null,
  };
}

export async function importKey(privateKeyArmored: string): Promise<KeyPair> {
  const privateKey = await openpgp.readPrivateKey({
    armoredKey: privateKeyArmored,
  });
  const publicKey = privateKey.toPublic();
  const id = privateKey.getKeyID().toHex().toUpperCase();
  const fingerprint = privateKey.getFingerprint().toUpperCase();

  const user = privateKey.getUserIDs()[0] || "";
  let name = "";
  let email = "";

  // Parse "Name <email>"
  const match = user.match(/^(.*) <(.*)>$/);
  if (match) {
    name = match[1];
    email = match[2];
  } else {
    name = user;
  }

  const creationTime = privateKey.getCreationTime();
  const expirationTime = await privateKey.getExpirationTime();

  return {
    id,
    fingerprint,
    name,
    email,
    privateKeyArmored,
    publicKeyArmored: publicKey.armor(),
    createdAt: new Date(creationTime).toISOString(),
    expiresAt:
      expirationTime && expirationTime !== Infinity
        ? new Date(expirationTime).toISOString()
        : null,
    registryStatus: null,
  };
}

/**
 * Backfill missing fingerprints for older KeyPairs (from prior versions).
 * Useful since getStoredKeys() is sync, so consumers can map/await this on demand.
 */
export async function backfillKey(key: KeyPair): Promise<KeyPair> {
  if (key.fingerprint) return key;
  const pubKey = await openpgp.readKey({ armoredKey: key.publicKeyArmored });
  const fingerprint = pubKey.getFingerprint().toUpperCase();

  const updated: KeyPair = {
    ...key,
    fingerprint,
    registryStatus: key.registryStatus || null,
  };

  // Auto-save the backfilled key to vault
  saveKey(updated);
  return updated;
}

/**
 * Sign a string (like a document hash) using a PGP private key.
 * Now executed purely in an isolated Web Worker to protect
 * the decrypted RSA keys from XSS attacks traversing the main V8 heap.
 */
export async function signData(
  data: string,
  privateKeyArmored: string,
  passphrase?: string,
): Promise<string> {
  return new Promise((resolve, reject) => {
    // 1. Boot up the Black Box Signer worker
    const worker = new Worker(
      new URL("../workers/pgp-signer.worker.ts", import.meta.url),
    );

    // 2. Await the PGP signature string
    worker.onmessage = (e) => {
      const { type, signature, error } = e.data;

      // Shut down the worker entirely to flush memory
      worker.terminate();

      if (type === "SUCCESS") {
        resolve(signature);
      } else {
        reject(new Error(error || "Worker signing failed"));
      }
    };

    worker.onerror = (err) => {
      worker.terminate();
      reject(new Error(err.message || "Failed to start signing worker"));
    };

    // 3. Send encrypted payload securely to isolated context
    worker.postMessage({
      action: "SIGN_DATA",
      payload: data,
      privateKeyArmored,
      passphrase,
    });
  });
}

/**
 * Verify a signature against a string using a PGP public key.
 */
export async function verifyData(
  data: string,
  signatureArmored: string,
  publicKeyArmored: string,
): Promise<boolean> {
  const publicKey = await openpgp.readKey({ armoredKey: publicKeyArmored });
  const message = await openpgp.createMessage({ text: data });
  const verification = await openpgp.verify({
    message,
    signature: await openpgp.readSignature({
      armoredSignature: signatureArmored,
    }),
    verificationKeys: publicKey,
  });

  const { verified } = verification.signatures[0];
  try {
    await verified; // throws on invalid signature
    return true;
  } catch (e) {
    return false;
  }
}
